import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { ManagementService } from './management.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, Observable } from 'rxjs';
import { StateService } from '../../services/state.service';
import { Account, Brand, User } from './types';
import { PaginatorComponent } from '../../shared/components/paginator/paginator.component';
import { PageEvent } from '@angular/material/paginator';
import { SearchBarComponent } from '../../shared/components/search-bar/search-bar.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { FormControl } from '@angular/forms';
import { UserModalComponent } from './user-modal/user-modal.component';
import { AccountModalComponent } from './account-modal/account-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { BrandModalComponent } from './brand-modal/brand-modal.component';
import { UtilService } from '../../services/util.service';
import { Router, RouterOutlet, ActivatedRoute } from '@angular/router';

const BRAND_PAGE_SIZE = 15;
const USERS_PAGE_SIZE = 10;
const ACCOUNTS_PAGE_SIZE = 10;

type TabConfig = {
  pageSize: number;
  activeButtonText: string;
  fetchData: (term?: string) => Observable<any>;
  setData: (data: Brand[] | User[] | Account[]) => void;
  add: () => void;
};

@Component({
  selector: 'app-management-screen',
  imports: [CommonModule, RouterOutlet, PaginatorComponent, SearchBarComponent, ButtonComponent],
  templateUrl: './management-screen.component.html',
  styleUrl: './management-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManagementScreenComponent {

  public activeButtonText: string = '';
  public tabConfigs: Record<string, TabConfig> = {
    users: {
      pageSize: USERS_PAGE_SIZE,
      activeButtonText: "+ Add User",
      fetchData: () => this.managementService.getUsers({
        limit: this.pageSize,
        offset: this.currentPage * this.pageSize,
        term: this.searchTerm.value,
        sortBy: this.managementService.sortState().sortBy,
        sortDir: this.managementService.sortState().sortDir
      }),
      setData: (data) => this.managementService.tableData.set(data),
      add: () => this.addUser()
    },
    brands: {
      pageSize: BRAND_PAGE_SIZE,
      activeButtonText: '+ Add Brand',
      fetchData: () => this.managementService.getBrands({
        limit: this.pageSize,
        offset: this.currentPage * this.pageSize,
        elaborate: true,
        term: this.searchTerm.value,
        sortBy: this.managementService.sortState().sortBy,
        sortDir: this.managementService.sortState().sortDir
      }),
      setData: (data) => this.managementService.tableData.set(data as Brand[]),
      add: () => this.addBrand()
    },
    accounts: {
      pageSize: ACCOUNTS_PAGE_SIZE,
      activeButtonText: '+ Add Account',
      fetchData: () => this.managementService.getAccounts({
        limit: this.pageSize,
        offset: this.currentPage * this.pageSize,
        term: this.searchTerm.value,
        elaborate: true,
        sortBy: this.managementService.sortState().sortBy,
        sortDir: this.managementService.sortState().sortDir
      }),
      setData: (data) => this.managementService.tableData.set(data),
      add: () => this.addAccount()
    }
  };
  public tabs: string[] = ['accounts', 'users', 'brands'];
  public pageSize = this.getPageSize(this.tabs[0]);
  public totalRecords: number = 0;
  public initialPage: number = 1;
  public currentPage: number = 0;
  public searchTerm: FormControl<string> = new FormControl('') as FormControl<string>;
  private destroyRef = inject(DestroyRef);
  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;



  constructor(private managementService: ManagementService, private stateService: StateService,
    private dialog: MatDialog, private utilService: UtilService, private router: Router,
    private route: ActivatedRoute, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    forkJoin({
      userRoles: this.managementService.getUserRoles(),
      brands: this.managementService.getBrands({ elaborate: false }),
      accounts: this.managementService.getAccounts({ elaborate: false }),
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ userRoles, brands, accounts }) => {
      this.managementService.userRoles = userRoles.roles;
      this.managementService.brandsList = brands.brands;
      this.managementService.accountList = accounts.accounts;
      this.switchTab(this.activeTab);
    });

    this.managementService.refreshTable$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.fetchTableData();
    });
  }

  switchTab(tab: string): void {
    this.tabConfigs[this.activeTab].setData([]);
    this.stateService.loading = true;
    this.router.navigate([tab], { relativeTo: this.route });
    this.setActiveButtonText(tab);
    this.pageSize = this.getPageSize(tab);
    this.setCurrentPage(0);
    this.searchTerm.setValue('');
    this.managementService.sortState.set({ sortBy: '', sortDir: 'desc' });
    this.stateService.loading = false;
  }

  fetchTableData(): void {
    this.stateService.loading = true;
    this.tabConfigs[this.activeTab].fetchData()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.stateService.loading = false)
      )
      .subscribe(result => {
        this.tabConfigs[this.activeTab].setData(result[this.activeTab]);
        this.totalRecords = result.total;
        this.cdr.detectChanges();
      });
  }

  onPageChange(event: PageEvent) {
    this.setCurrentPage(event.pageIndex);
    this.fetchTableData();
  }

  setCurrentPage(page: number) {
    this.currentPage = page;
    this.paginator.currentPage = page + 1;
  }

  getPageSize(tab: string): number {
    return this.tabConfigs[tab].pageSize;
  }

  setActiveButtonText(tab: string): void {
    this.activeButtonText = this.tabConfigs[tab].activeButtonText;
  }

  search(): void {
    this.setCurrentPage(0);
    this.fetchTableData();
  }

  addUser() {
    const dialogRef = this.dialog.open(UserModalComponent, {
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result) {
        this.searchTerm.setValue('');
        this.search();
        this.utilService.openSnackBar(`User "${result.firstName}" added successfuly`, { position: 'center', variant: 'success' });
      }
    })
  }

  addBrand() {
    const dialogRef = this.dialog.open(BrandModalComponent, {
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result) {
        this.searchTerm.setValue(result.name);
        this.search();
        this.managementService.updateBrandInList({ name: result.name, brandKey: result.brandKey });
        this.utilService.openSnackBar(`Brand "${result.name}" added successfuly`, { position: 'center', variant: 'success' });
      }
      this.stateService.loading = false;
    });
  }

  addAccount() {
    const dialogRef = this.dialog.open(AccountModalComponent, {
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result) {
        this.searchTerm.setValue('');
        this.search();
        this.utilService.openSnackBar(`Account "${result.name}" added successfuly`, { position: 'center', variant: 'success' });
      }
      this.stateService.loading = false;
    });
  }

  get activeTab(): string {
    return this.route.snapshot.firstChild?.url[0].path || '';
  }
}
